import styled, { css } from 'styled-components';

// RTL-aware container
export const RTLContainer = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
`;

// RTL-aware flex container
export const RTLFlexContainer = styled.div<{ 
  isRTL?: boolean; 
  direction?: 'row' | 'column';
  justify?: string;
  align?: string;
}>`
  display: flex;
  flex-direction: ${props => {
    if (props.direction === 'column') return 'column';
    return props.isRTL ? 'row-reverse' : 'row';
  }};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'stretch'};
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
`;

// RTL-aware text component
export const RTLText = styled.p<{ 
  isRTL?: boolean; 
  size?: string;
  weight?: string;
  color?: string;
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  font-size: ${props => props.size || '14px'};
  font-weight: ${props => props.weight || '400'};
  color: ${props => props.color || 'inherit'};
  line-height: ${props => props.isRTL ? '1.8' : '1.6'};
  
  ${props => props.isRTL && css`
    font-feature-settings: "liga" 1, "kern" 1;
    text-rendering: optimizeLegibility;
  `}
`;

// RTL-aware button
export const RTLButton = styled.button<{ 
  isRTL?: boolean;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  padding: ${props => {
    switch (props.size) {
      case 'small': return '6px 12px';
      case 'large': return '12px 24px';
      default: return '8px 16px';
    }
  }};
  
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return css`
          background-color: #f5f7fa;
          color: #2c71ad;
          border: 1px solid #e9edf3;
          
          &:hover {
            background-color: #e9edf3;
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: #2c71ad;
          border: 1px solid #2c71ad;
          
          &:hover {
            background-color: #2c71ad;
            color: white;
          }
        `;
      default:
        return css`
          background-color: #2c71ad;
          color: white;
          
          &:hover {
            background-color: #41a7ff;
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  // Icon positioning for RTL
  .icon {
    ${props => props.isRTL && css`
      transform: scaleX(-1);
    `}
  }
`;

// RTL-aware input field
export const RTLInput = styled.input<{ 
  isRTL?: boolean;
  hasError?: boolean;
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#ff4d4f' : '#d9d9d9'};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #2c71ad;
    box-shadow: 0 0 0 2px rgba(44, 113, 173, 0.1);
  }
  
  &::placeholder {
    color: #8c8c8c;
    text-align: ${props => props.isRTL ? 'right' : 'left'};
  }
  
  ${props => props.isRTL && css`
    font-feature-settings: "liga" 1, "kern" 1;
  `}
`;

// RTL-aware form group
export const RTLFormGroup = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  margin-bottom: 16px;
`;

// RTL-aware label
export const RTLLabel = styled.label<{ 
  isRTL?: boolean;
  required?: boolean;
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  
  ${props => props.required && css`
    &::after {
      content: ' *';
      color: #ff4d4f;
      margin-${props.isRTL ? 'right' : 'left'}: 4px;
    }
  `}
`;

// RTL-aware card component
export const RTLCard = styled.div<{ 
  isRTL?: boolean;
  padding?: string;
  shadow?: boolean;
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  background: white;
  border-radius: 8px;
  padding: ${props => props.padding || '24px'};
  border: 1px solid #e9edf3;
  
  ${props => props.shadow && css`
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `}
`;

// RTL-aware navigation item
export const RTLNavItem = styled.div<{ 
  isRTL?: boolean;
  active?: boolean;
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  
  ${props => props.active && css`
    background-color: #f0f7ff;
    color: #2c71ad;
  `}
  
  &:hover {
    background-color: #f5f7fa;
  }
  
  .icon {
    margin-${props => props.isRTL ? 'left' : 'right'}: 12px;
    ${props => props.isRTL && css`
      transform: scaleX(-1);
    `}
  }
`;

// RTL-aware modal
export const RTLModal = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// RTL-aware modal content
export const RTLModalContent = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e9edf3;
    
    ${props => props.isRTL && css`
      flex-direction: row-reverse;
    `}
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #8c8c8c;
    
    &:hover {
      color: #333;
    }
  }
`;

// RTL-aware table
export const RTLTable = styled.table<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  width: 100%;
  border-collapse: collapse;
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  th, td {
    text-align: ${props => props.isRTL ? 'right' : 'left'};
    padding: 12px 16px;
    border-bottom: 1px solid #e9edf3;
  }
  
  th {
    background-color: #f5f7fa;
    font-weight: 600;
    color: #333;
  }
  
  tr:hover {
    background-color: #f9f9f9;
  }
`;

// RTL-aware alert/notification
export const RTLAlert = styled.div<{ 
  isRTL?: boolean;
  type?: 'success' | 'error' | 'warning' | 'info';
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  font-family: ${props => props.isRTL 
    ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
    : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
  };
  
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  ${props => props.isRTL && css`
    flex-direction: row-reverse;
  `}
  
  ${props => {
    switch (props.type) {
      case 'success':
        return css`
          background-color: #f6ffed;
          border: 1px solid #b7eb8f;
          color: #389e0d;
        `;
      case 'error':
        return css`
          background-color: #fff2f0;
          border: 1px solid #ffccc7;
          color: #cf1322;
        `;
      case 'warning':
        return css`
          background-color: #fffbe6;
          border: 1px solid #ffe58f;
          color: #d48806;
        `;
      default:
        return css`
          background-color: #e6f7ff;
          border: 1px solid #91d5ff;
          color: #0958d9;
        `;
    }
  }}
`;

// RTL-aware main content layout
export const RTLMainContent = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  
  .content-wrapper {
    display: flex;
    flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
    width: 100%;
    height: 100%;
  }
  
  .main-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: ${props => props.isRTL ? '0 0.5rem 0 1rem' : '0 1rem 0 0.5rem'};
    
    @media (max-width: 768px) {
      padding: 0.5rem;
    }
  }
  
  .side-section {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    margin: ${props => props.isRTL ? '0 1.25rem 0 0' : '0 0 0 1.25rem'};
    
    @media (max-width: 768px) {
      margin: 0;
      margin-top: 1rem;
    }
  }
`;

// RTL-aware flex container for cards and content
export const RTLContentSection = styled.div<{ 
  isRTL?: boolean; 
  layout?: 'main' | 'sidebar' | 'full';
}>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  display: flex;
  flex-direction: column;
  width: 100%;
  
  ${props => props.layout === 'main' && css`
    width: ${props.isRTL ? '66.666667%' : '66.666667%'}; /* 2/3 width */
    padding-${props.isRTL ? 'left' : 'right'}: 0;
    padding-${props.isRTL ? 'right' : 'left'}: 0.5rem;
    
    @media (max-width: 768px) {
      width: 75%;
      padding: 0.5rem;
    }
  `}
  
  ${props => props.layout === 'sidebar' && css`
    width: ${props.isRTL ? '33.333333%' : '33.333333%'}; /* 1/3 width */
    margin-${props.isRTL ? 'right' : 'left'}: 1.25rem;
    
    @media (max-width: 768px) {
      width: 25%;
      margin: 0;
      margin-top: 1.75rem;
    }
  `}
  
  ${props => props.layout === 'full' && css`
    width: 100%;
    padding: 0.5rem;
  `}
`;

// RTL-aware Card container
export const RTLCardContainer = styled.div<{ isRTL?: boolean }>`
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  
  .card-content {
    text-align: ${props => props.isRTL ? 'right' : 'left'};
    
    * {
      font-family: ${props => props.isRTL 
        ? "'Noto Sans Arabic', 'Cairo', 'Tajawal', sans-serif" 
        : "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
      };
    }
  }
  
  .form-field {
    margin-bottom: 1rem;
    
    label {
      text-align: ${props => props.isRTL ? 'right' : 'left'};
      margin-bottom: 0.5rem;
      display: block;
    }
    
    input, textarea, select {
      direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
      text-align: ${props => props.isRTL ? 'right' : 'left'};
    }
  }
  
  .button-group {
    display: flex;
    justify-content: ${props => props.isRTL ? 'flex-start' : 'flex-end'};
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

// RTL-aware utility classes generator
export const getRTLClasses = (isRTL: boolean) => ({
  flexRow: isRTL ? 'flex flex-row-reverse' : 'flex flex-row',
  textAlign: isRTL ? 'text-right' : 'text-left',
  marginLeft: (size: string) => isRTL ? `mr-${size}` : `ml-${size}`,
  marginRight: (size: string) => isRTL ? `ml-${size}` : `mr-${size}`,
  paddingLeft: (size: string) => isRTL ? `pr-${size}` : `pl-${size}`,
  paddingRight: (size: string) => isRTL ? `pl-${size}` : `pr-${size}`,
  floatLeft: isRTL ? 'float-right' : 'float-left',
  floatRight: isRTL ? 'float-left' : 'float-right',
});

export default {
  RTLContainer,
  RTLFlexContainer,
  RTLText,
  RTLButton,
  RTLInput,
  RTLFormGroup,
  RTLLabel,
  RTLCard,
  RTLNavItem,
  RTLModal,
  RTLModalContent,
  RTLTable,
  RTLAlert
}; 